import Close from "@mui/icons-material/Close";
import Error from "@mui/icons-material/Error";
import KeyboardArrowRightRounded from "@mui/icons-material/KeyboardArrowRightRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import isNil from "lodash/isNil";
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";

import { AddCommentOutlined } from "@mui/icons-material";
import { Badge } from "@mui/material";
import { blue } from "@mui/material/colors";
import { isEmpty, isFunction, size } from "lodash";
import FilePreviewImageTypeIcon from "../../../../components/common/FilePreviewImageTypeIcon";
import { isSpreadsheetFile } from "../../../../components/common/isSpreadsheetFile";
import PinnedCommentsModal from "../../../../components/common/Loan/Comments/PinnedCommentsModal";
import Txt from "../../../../components/typography/Txt";
import { useLoanFilesHook } from "../../../../hooks/useLoanFilesHook";
import { useCommentUtilsHook } from "../../../../hooks/utils/useCommentUtilsHook";
import DownloadLoanFileIconButton from "../../shared/DownloadLoanFileIconButton";
import OpenLoanFileInNewTabButton from "../../shared/OpenLoanFileInNewTabButton";
import ObjectInfoTooltip from "../../../../components/data/ObjectInfoTooltip";

// Heavy (xlsx + AG Grid) — only loaded when an Excel file is previewed.
const ExcelPreview = lazy(
  () => import("../../../../components/common/ExcelPreview"),
);

/**
 * LoanFilePreview
 * Renders a file preview (image/PDF/Excel/other) and optionally loads associated comments.
 */
const LoanFilePreview = ({
  fileObject,
  onClose,
  loan,
  showDownloadButton = true,
  height = "100%",
  width = "100%",
  containerId,
  headerSx,
  commentsDefaultOpened = false,
  sx = {},
}) => {
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  // Raw bytes for the spreadsheet parser (see ExcelPreview) — parsed directly
  // rather than by re-fetching a possibly-revoked object URL.
  const [previewBlob, setPreviewBlob] = useState(null);
  const { loading, getFileById } = useLoanFilesHook();
  const { getFileAssociatedComments } = useCommentUtilsHook();
  const [showComments, setShowComments] = useState(commentsDefaultOpened);
  const content_type = fileObject?.content_type;
  const file_display_name = fileObject?.file_display_name;
  const file_storage_name = fileObject?.file_storage_name;
  const fileId = fileObject?._id;
  const loanId = containerId || fileObject?.dealId || fileObject?.containerName;
  // const loanId = fileObject?.dealId || containerId || fileObject?.containerName;

  const isImage = content_type?.startsWith("image/");
  const isPdf = content_type === "application/pdf";
  const isExcel = isSpreadsheetFile({
    content_type,
    fileName: file_display_name,
  });

  // const relatedComments = getCommentsByDocumentId(loan, fileObject?._id);
  const relatedComments = getFileAssociatedComments(loan?.comments, fileId);

  const handleOnClose = useCallback(() => {
    setShowComments(commentsDefaultOpened);
    if (isFunction(onClose)) {
      onClose();
    }
  }, [commentsDefaultOpened]);
  // console.log("relatedComments", { relatedComments, loanComments, fileObject });
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    if (fileObject && file_storage_name && loanId) {
      setShowComments(commentsDefaultOpened);
      fetchFile();
    }
  }, [fileObject, commentsDefaultOpened]);

  const fetchFile = () => {
    setError(null);

    // console.log("FETCH", {
    //   loanId,
    //   file_storage_name,
    //   isFile: true,
    //   onSuccessFn: (responseData) => {
    //     try {
    //       const blob =
    //         responseData instanceof Blob
    //           ? responseData
    //           : new Blob([responseData], { type: content_type });
    //       const url = URL.createObjectURL(blob);
    //       setPreviewUrl(url);
    //     } catch (err) {
    //       console.error("Error creating Blob URL:", err);
    //       setError(
    //         "Failed to process the file. The format may be unsupported.",
    //       );
    //     }
    //   },
    //   onErrorFn: (err) => {
    //     console.error("Error fetching file:", err);
    //     setError("Failed to load the file. Please try again.");
    //   },
    // });
    // return;
    getFileById({
      loanId,
      file_storage_name,
      isFile: true,
      onSuccessFn: (responseData) => {
        try {
          const blob =
            responseData instanceof Blob
              ? responseData
              : new Blob([responseData], { type: content_type });
          setPreviewBlob(blob);
          const url = URL.createObjectURL(blob);
          setPreviewUrl(url);
        } catch (err) {
          console.error("Error creating Blob URL:", err);
          setError(
            "Failed to process the file. The format may be unsupported.",
          );
        }
      },
      onErrorFn: (err) => {
        console.error("Error fetching file:", err);
        setError("Failed to load the file. Please try again.");
      },
    });
  };

  const headerUtilStyles = useMemo(() => {
    const sharedStyles = {
      display: "flex",
      gap: 0.5,
      flexShrink: 0,
      ml: 1,

      ".MuiSvgIcon-root": {
        fontSize: "16px",
      },
    };
    const customBadgeStyles = {
      ".MuiBadge-badge": {
        minWidth: "16px",
        height: "16px",
        fontSize: "10px",
        transform: "scale(1) translate(50%, -60%)",
      },
    };
    if (isEmpty(relatedComments)) {
      return { ...sharedStyles };
    }
    return {
      ...sharedStyles,
      ...customBadgeStyles,
    };
  }, [relatedComments]);

  if (isNil(fileObject)) return null;

  // Empty state when no file selected
  if (!fileObject) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height,
          width,
          p: 3,
          ...sx,
        }}
      >
        <FilePreviewImageTypeIcon contentType={content_type} />
        <Typography variant="body1" sx={{ mt: 2, color: "text.secondary" }}>
          No file selected for preview
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {showComments && (
        <PinnedCommentsModal
          title="File Comments"
          show={showComments}
          comments={relatedComments}
          loan={loan}
          documentIds={[fileId]}
          onClose={() => setShowComments(false)}
        />
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height,
          background: "#ffffff",
          position: "relative",
          overflow: "hidden",
          ...sx,
        }}
      >
        {/* Header */}
        {/* <Flx column sx={{ background: "#ffffff", p: 0.5, pl: 1, ...headerSx }}> */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            // p: 1.5,
            py: 0.8,
            px: 0.5,
            pl: 1,
            //
            background: "#ffffff",
            ...headerSx,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: 0, // allow this container to shrink
              flexGrow: 1,
              gap: 1,
              overflow: "hidden", // clip any overflow
            }}
          >
            <FilePreviewImageTypeIcon
              contentType={content_type}
              size={28}
              color="action"
              sx={{ flexShrink: 0 }}
            />

            {/* make this flex‐column container shrinkable */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                flexGrow: 1,
                mt: "-0.5px",
                overflow: "hidden", // important so children ellipsize
              }}
            >
              <Tooltip title={file_display_name || "File Preview"}>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 700,
                    overflow: "hidden", // clip overflow
                    textOverflow: "ellipsis", // show “…”
                    whiteSpace: "nowrap", // single line
                    width: "100%", // fill the parent box
                  }}
                >
                  {file_display_name || "File Preview"}
                </Typography>
              </Tooltip>

              {isNil(fileObject?.docGroup) &&
              isNil(fileObject?.docType) ? null : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    flexShrink: 0,
                  }}
                >
                  <Txt
                    secondary
                    sx={{
                      fontSize: "10px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "300px",
                    }}
                  >
                    {fileObject?.docGroup || "N/A"}
                  </Txt>
                  <KeyboardArrowRightRounded sx={{ fontSize: "10px" }} />
                  <Txt
                    secondary
                    sx={{
                      fontSize: "10px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "300px",
                    }}
                  >
                    {fileObject?.docType || "N/A"}
                  </Txt>
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={headerUtilStyles}>
            <IconButton
              sx={{ background: showComments ? blue[50] : undefined }}
              onClick={() => setShowComments((s) => !s)}
              color="primary"
              size="small"
            >
              <Badge badgeContent={size(relatedComments)} color="primary">
                <AddCommentOutlined />
              </Badge>
            </IconButton>

            <OpenLoanFileInNewTabButton
              loanId={loanId}
              fileObject={fileObject}
            />
            <DownloadLoanFileIconButton
              loanId={loanId}
              fileObject={fileObject}
              showDownloadButton={showDownloadButton}
            />
            {/* <FileInfoTooltip file={fileObject} /> */}
            <ObjectInfoTooltip
              title="File Details"
              format={[
                { field: "modifiedDate", type: "date" },
                { field: "uploadDate", type: "date" },
                { field: "reportEffectiveDate", type: "date" },
                { field: "uploadDate", type: "date" },
              ]}
              data={fileObject}
              excludeKeys={[
                "_id",
                "content_type",
                "container_type",
                "dealId",
                "file_storage_name",
                "containerName",
              ]}
            />
            {onClose && (
              <>
                <Divider orientation="vertical" flexItem sx={{ ml: 0.5 }} />
                <IconButton
                  onClick={handleOnClose}
                  color="inherit"
                  size="small"
                >
                  <Close sx={{ fontSize: "20px !important" }} />
                </IconButton>
              </>
            )}
          </Box>
        </Box>
        {/* </Flx> */}
        <Divider />

        {/* Preview Body */}
        <Box
          sx={{
            position: "relative",
            flexGrow: 1,

            overflow: "auto",
            bgcolor: (theme) => theme.palette.grey[50],
          }}
        >
          {/* Loading Overlay */}
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(0,0,0,0.6)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <CircularProgress sx={{ color: "#ffffff" }} />
                <Txt body2 sx={{ mt: 2, color: "#ffffff" }}>
                  Loading file...
                </Txt>
              </Box>
            </Box>
          )}
          {/* Error Overlay */}
          <ErrorOverlay error={error} onFetchFileClick={fetchFile} />
          {/* Actual Preview */}
          {!loading && !error && previewUrl && (
            <>
              {isImage && (
                <Box
                  sx={{
                    // p: 2,
                    display: "flex",
                    justifyContent: "center",
                    height: "100%",
                    background: "#282828",
                  }}
                >
                  <img
                    src={previewUrl}
                    alt={file_display_name || "Image preview"}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      border: "1px solid #ffffff85",
                      borderRadius: 2,
                    }}
                  />
                </Box>
              )}
              {isPdf && (
                <iframe
                  src={previewUrl}
                  title={file_display_name || "PDF preview"}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                    position: "absolute",
                    top: 0,
                    left: 0,
                  }}
                />
              )}
              {isExcel && (
                <Box sx={{ position: "absolute", inset: 0 }}>
                  <Suspense
                    fallback={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    }
                  >
                    <ExcelPreview
                      blob={previewBlob}
                      fileUrl={previewUrl}
                      fileName={file_display_name}
                      height="100%"
                    />
                  </Suspense>
                </Box>
              )}
              {!isImage && !isPdf && !isExcel && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    p: 3,
                  }}
                >
                  <FilePreviewImageTypeIcon contentType={content_type} />
                  <Typography
                    variant="body1"
                    sx={{ mt: 2, textAlign: "center" }}
                  >
                    This file type ({content_type}) cannot be previewed.
                  </Typography>
                  <DownloadLoanFileIconButton
                    loanId={loanId}
                    fileObject={fileObject}
                    showDownloadButton={showDownloadButton}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

/**
 * ErrorOverlay
 * Renders an overlay when there's an error fetching or processing the file.
 */
const ErrorOverlay = ({ error, onFetchFileClick }) => {
  if (isNil(error)) return null;
  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(255, 255, 255, 0.8)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          p: 2,
        }}
      >
        <Error color="error" sx={{ fontSize: 40 }} />
        <Typography variant="body1" color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
        <Button variant="contained" onClick={onFetchFileClick} sx={{ mt: 2 }}>
          Try Again
        </Button>
      </Box>
    </Box>
  );
};


export default LoanFilePreview;
